import type { CountryCode } from 'libphonenumber-js';
import React, { Activity, useMemo, type FC } from 'react';
import { StyleSheet, type TextStyle, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { Text, TextProps } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input/text-input';
import type {
  TextInputFieldProps,
  TextInputFlagProps,
  TextInputIconProps,
  TextInputStatus,
} from '@/components/ui/text-input/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { TypographyToken } from '@/theme/typography';

export type FormInputProps = Omit<TextInputFieldProps, 'style'> & {
  label: string;
  /** When true, only the field is shown (e.g. second address line aligned with another labeled field). */
  hideLabel?: boolean;
  /**
   * Show required asterisk (*) as in Figma
   */
  required?: boolean;
  status?: TextInputStatus;
  disabled?: boolean;
  /**
   * Icon on the right (default) or left of the input
   */
  iconProps?: TextInputIconProps;
  containerStyle?: ViewStyle | ViewStyle[];
  inputStyle?: TextStyle | TextStyle[];
  phoneNumber?: boolean;
  phoneDefaultCountry?: CountryCode;
  /**
   * Input value
   */
  value?: string | null;
  /**
   * Input onChangeText handler
   */
  onChangeText?: (text: string) => void;
  /**
   * Input onSubmitEditing handler
   */
  onSubmitEditing?: () => void;
  /**
   * Input onFocus handler
   */
  onFocus?: () => void;
  /**
   * Trim input value
   */
  trim?: boolean;
  labelProps?: Omit<TextProps, 'children' | 'style'>;
  errorMessage?: string;
  onCountryCodeChange?: (countryCode: CountryCode | undefined) => void;
  /**
   * Uppercase the label text. Defaults to `true` (current behaviour).
   * Set `false` to render the label exactly as passed (e.g. to match Figma).
   */
  uppercaseLabel?: boolean;
  /** Override the label typography variant (default `Font Inter/Subtitle/Regular 11px`). */
  labelVariant?: TypographyToken;
  /** Extra style merged into the label text (e.g. custom color). */
  labelStyle?: TextProps['style'];
  /** Extra style merged into the required asterisk. */
  requiredMarkStyle?: TextProps['style'];
  /** Gap (px) between the label row and the input box. Defaults to `8`. */
  labelGap?: number;
  /** Style merged into the input box container (border color/width, radius, height, padding…). */
  contentStyle?: ViewStyle | ViewStyle[];
  /** Extra style applied on top of the default input box look while disabled. */
  disableStyle?: ViewStyle | ViewStyle[];
  /** Override the input field typography variant (default `Font Inter/Subtitle/Regular 14px`). */
  inputVariant?: TypographyToken;
  /** Props forwarded to the phone country flag (variant, styleWrapper…). */
  flagProps?: TextInputFlagProps;
  /** Render a vertical divider between the flag and the field (phone mode only). */
  phoneDivider?: boolean;
  /** Style for the phone divider. */
  phoneDividerStyle?: ViewStyle | ViewStyle[];
};

export const FormInput: FC<FormInputProps> = ({
  label,
  hideLabel = false,
  required,
  status = 'default',
  disabled = false,
  iconProps,
  containerStyle,
  inputStyle,
  phoneNumber = false,
  phoneDefaultCountry,
  placeholder,
  trim = false,
  labelProps,
  onBlur,
  onFocus,
  errorMessage,
  uppercaseLabel = true,
  labelVariant = 'Font Inter/Subtitle/Regular 11px',
  labelStyle,
  requiredMarkStyle,
  labelGap = 8,
  contentStyle,
  disableStyle,
  inputVariant = 'Font Inter/Subtitle/Regular 14px',
  flagProps,
  phoneDivider = false,
  phoneDividerStyle,
  ...fieldProps
}) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const displayLabel = uppercaseLabel ? (label?.toUpperCase?.() ?? '') : (label ?? '');
  const resolvedPlaceholder = placeholder ?? label;

  const iconPosition = iconProps?.position ?? 'right';

  const computedInputStyle = useMemo<TextStyle[]>(() => {
    const stylesArray: TextStyle[] = [{ paddingHorizontal: scale(0), marginHorizontal: scale(0) }];

    if (iconPosition === 'left' || phoneNumber) {
      stylesArray.push(styles.noIconRightTextInputStyle);
    } else if (iconPosition === 'right') {
      stylesArray.push(styles.noIconLeftTextInputStyle);
    }

    if (Array.isArray(inputStyle)) {
      stylesArray.push(...inputStyle);
    } else if (inputStyle) {
      stylesArray.push(inputStyle);
    }

    return stylesArray;
  }, [
    iconPosition,
    inputStyle,
    phoneNumber,
    scale,
    styles.noIconLeftTextInputStyle,
    styles.noIconRightTextInputStyle,
  ]);

  return (
    <Stack direction="column" gap={hideLabel ? 0 : labelGap} width="100%" style={containerStyle}>
      {!hideLabel && (
        <Stack direction="row" align="center" justify="flex-start">
          <TextInput.Label
            variant={labelVariant}
            style={[styles.label, labelStyle]}
            {...labelProps}>
            {displayLabel}
          </TextInput.Label>
          {required && (
            <Text variant={labelVariant} style={[styles.requiredMark, requiredMarkStyle]}>
              *
            </Text>
          )}
        </Stack>
      )}

      <TextInput
        status={disabled ? 'disabled' : status}
        disabled={disabled}
        value={fieldProps.value ?? undefined}
        onChangeText={fieldProps.onChangeText}
        onCountryCodeChange={fieldProps.onCountryCodeChange}
        onSubmitEditing={() => {
          if (trim) {
            fieldProps.onChangeText?.(fieldProps.value?.trim() ?? '');
          }
          fieldProps.onSubmitEditing?.();
        }}
        onBlur={e => {
          if (trim) {
            fieldProps.onChangeText?.(fieldProps.value?.trim() ?? '');
          }
          onBlur?.(e);
        }}
        onFocus={onFocus}
        phoneNumber={phoneNumber}
        phoneDefaultCountry={phoneDefaultCountry}>
        {iconProps && iconPosition === 'left' && <TextInput.Icon {...iconProps} position="left" />}
        <TextInput.Content
          paddingX={0}
          marginX={0}
          style={[
            { paddingHorizontal: scale(6) },
            contentStyle,
            disabled ? disableStyle : undefined,
          ]}>
          {phoneNumber && (
            <TextInput.Flag
              position="left"
              {...flagProps}
              styleWrapper={StyleSheet.flatten([{ marginLeft: scale(8) }, flagProps?.styleWrapper])}
            />
          )}
          {phoneNumber && phoneDivider && <TextInput.Divider style={phoneDividerStyle} />}
          <TextInput.Field
            {...fieldProps}
            variant={inputVariant}
            placeholder={resolvedPlaceholder}
            style={computedInputStyle}
          />
          {iconProps && iconPosition === 'right' && (
            <TextInput.Icon {...iconProps} position="right" />
          )}
        </TextInput.Content>
        <Activity mode={errorMessage ? 'visible' : 'hidden'}>
          <TextInput.HelperText>{errorMessage}</TextInput.HelperText>
        </Activity>
      </TextInput>
    </Stack>
  );
};

const useStyles = createStyles(({ scale }) => ({
  noIconTextInputStyle: {
    marginHorizontal: scale(16),
  },
  noIconLeftTextInputStyle: {
    marginLeft: scale(8),
  },
  noIconRightTextInputStyle: {
    marginRight: scale(16),
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: scale(8),
  },
  label: {
    color: colors['Labels/Primary'],
  },
  requiredMark: {
    marginLeft: scale(4),
    color: colors.Red,
  },
}));
